import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";

import {
  FiSearch,
  FiAlertCircle,
  FiClipboard,
  FiUser,
  FiClock
} from "react-icons/fi";

import GuruLayout from "../../components/layout/GuruLayout";

const HARI_ORDER = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
const API_URL = "https://backend.smpn1kamal.sch.id/api";

export default function JadwalGuru() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");

  const getAuthHeader = () => ({ Authorization: "Bearer " + token });

  const [activeTab, setActiveTab] = useState("jadwal");
  const [cari, setCari] = useState("");
  const [loading, setLoading] = useState(false);

  const [jadwalMingguan, setJadwalMingguan] = useState([]);
  const [jadwalKosong, setJadwalKosong] = useState([]);
  const [jadwalAmbilan, setJadwalAmbilan] = useState([]);
  const [statusLaporan, setStatusLaporan] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [showModalAll, setShowModalAll] = useState(false);
  const [selJadwal, setSelJadwal] = useState(null);
  const [alasan, setAlasan] = useState("");
  const [tanggalIzin, setTanggalIzin] = useState(new Date().toISOString().slice(0, 10));
  const [tanggalIzinSingle, setTanggalIzinSingle] = useState(new Date().toISOString().slice(0, 10));

  const formatJam = (jam) => jam ? jam.slice(0, 5).replace(":", ".") : "";
  const formatHariTanggal = (tanggal) => {
    if (!tanggal) return "-";
    return new Date(tanggal).toLocaleDateString("id-ID", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  };

  const fetchData = useCallback(async (tab) => {
    setLoading(true);
    try {
      const endpoints = {
        jadwal: "/jadwal/guru/me",
        kosong: "/pengganti/kosong",
        ambilan: "/pengganti/saya",
        status: "/pengganti/status-laporan",
      };
      const response = await axios.get(API_URL + endpoints[tab], { headers: getAuthHeader() });
      let data = response.data.data || response.data;
      
      if (tab === "jadwal") setJadwalMingguan(data);
      else if (tab === "kosong") setJadwalKosong(data);
      else if (tab === "ambilan") setJadwalAmbilan(data);
      else if (tab === "status") setStatusLaporan(data);
    } catch (error) {
      toast.error("Gagal sinkronisasi data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(activeTab); }, [activeTab, fetchData]);

  const handleLapor = async (modeType) => {
    try {
      let endpoint = modeType === "single" ? "/pengganti/lapor" : "/pengganti/lapor-semua";
      let payload = modeType === "single" 
        ? { mode: "pilihan", id_jadwal_list: [selJadwal.id_jadwal], alasan, tanggal: tanggalIzinSingle }
        : { alasan, tanggal: tanggalIzin };
      await axios.post(API_URL + endpoint, payload, { headers: getAuthHeader() });
      toast.success("Laporan terkirim");
      setShowModal(false); setShowModalAll(false); setAlasan("");
      fetchData(activeTab);
    } catch (err) {
      toast.error(err.response?.data?.message || "Gagal melapor");
    }
  };

  const handleAmbilJadwal = async (id_pengganti) => {
    try {
      await axios.post(API_URL + "/pengganti/ambil", { id_pengganti }, { headers: getAuthHeader() });
      toast.success("Jadwal diambil");
      fetchData("kosong"); fetchData("ambilan");
    } catch (err) {
      toast.error("Gagal mengambil jadwal");
    }
  };

  const filteredJadwal = jadwalMingguan.filter(j => 
    j.nama_mapel?.toLowerCase().includes(cari.toLowerCase()) || 
    j.nama_kelas?.toLowerCase().includes(cari.toLowerCase())
  ).sort((a, b) => HARI_ORDER.indexOf(a.hari) - HARI_ORDER.indexOf(b.hari) || a.jam_mulai.localeCompare(b.jam_mulai));

  return (
    <GuruLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-center">
          <div>
            <h1 className="text-2xl font-bold text-indigo-600">Jadwal Mengajar</h1>
            <p className="text-sm text-gray-500">Manajemen kehadiran pengajar</p>
          </div>
          {activeTab === "jadwal" && (
            <button onClick={() => setShowModalAll(true)} className="bg-red-500 text-white px-5 py-2 rounded-full text-sm font-bold shadow-lg hover:bg-red-600 transition w-full sm:w-auto flex items-center justify-center gap-2">
              <FiAlertCircle /> Lapor Izin Satu Hari
            </button>
          )}
        </div>

        {/* TAB NAVIGATION - GRID 2X2 BIRU SOFT */}
        <div className="grid grid-cols-2 lg:flex lg:gap-2 gap-2 border-b border-gray-100 pb-4">
          {[
            { id: "jadwal", label: "Jadwal Rutin" },
            { id: "kosong", label: "Jadwal Kosong" },
            { id: "ambilan", label: "Tugas Pengganti" },
            { id: "status", label: "Riwayat Laporan" },
          ].map((t) => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} className={`px-4 py-3 text-xs sm:text-sm font-bold rounded-xl transition-all border ${activeTab === t.id ? "bg-indigo-600 text-white border-indigo-600 shadow-md" : "bg-indigo-50 text-indigo-600 border-indigo-100 hover:bg-indigo-100"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-md p-4 sm:p-5 min-h-[400px]">
          {activeTab === "jadwal" && (
            <div className="flex justify-end mb-5">
              <div className="relative w-full sm:w-64">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" placeholder="Cari..." className="border rounded-full pl-10 pr-4 py-2 text-sm w-full outline-none focus:ring-2 focus:ring-indigo-500" value={cari} onChange={(e) => setCari(e.target.value)} />
              </div>
            </div>
          )}

          {loading ? <div className="text-center py-20 text-gray-400">Memuat data...</div> : (
            <div>
              {/* DESKTOP VIEW */}
              <div className="hidden md:block overflow-x-auto rounded-xl">
                <table className="w-full text-sm min-w-[900px]">
                  <thead className="bg-gray-100 text-gray-600">
                    <tr>
                      <th className="px-4 py-3 text-left">HARI / TGL</th>
                      <th className="px-4 py-3 text-left">WAKTU</th>
                      <th className="px-4 py-3 text-left">MATA PELAJARAN</th>
                      <th className="px-4 py-3 text-left">KELAS</th>
                      {activeTab === "status" && <th className="px-4 py-3 text-left text-indigo-600">PENGISI</th>}
                      <th className="px-4 py-3 text-center">{activeTab === "status" ? "STATUS" : "AKSI"}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {activeTab === "jadwal" && filteredJadwal.map((item, i) => {
                      const isNewDay = i === 0 || item.hari !== filteredJadwal[i - 1].hari;
                      return (
                        <tr key={i} className="hover:bg-gray-50 transition">
                          <td className={`px-4 py-4 font-bold ${!isNewDay ? "text-transparent" : "text-gray-800"}`}>{item.hari}</td>
                          <td className="px-4 py-4">{formatJam(item.jam_mulai)} – {formatJam(item.jam_selesai)}</td>
                          <td className="px-4 py-4 font-medium">{item.nama_mapel}</td>
                          <td className="px-4 py-4 font-bold text-indigo-600 uppercase">{item.nama_kelas}</td>
                          <td className="px-4 py-4 text-center">
                            <button onClick={() => { setSelJadwal(item); setShowModal(true); }} className="text-red-500 border border-red-100 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-red-50">Lapor Izin</button>
                          </td>
                        </tr>
                      );
                    })}

                    {activeTab === "kosong" && jadwalKosong.map((item, i) => (
                      <tr key={i} className="hover:bg-amber-50 transition">
                        <td className="px-4 py-4 font-bold text-amber-600">{formatHariTanggal(item.tanggal)}</td>
                        <td className="px-4 py-4">{formatJam(item.jam_mulai)} – {formatJam(item.jam_selesai)}</td>
                        <td className="px-4 py-4 font-medium">
                          {item.nama_mapel}
                          <span className="block text-[10px] text-gray-400">Guru: {item.nama_guru_asli}</span>
                          {item.alasan && <span className="block text-[11px] text-red-500 italic">Alasan: {item.alasan}</span>}
                        </td>
                        <td className="px-4 py-4 font-bold uppercase">{item.nama_kelas}</td>
                        <td className="px-4 py-4 text-center">
                          <button onClick={() => handleAmbilJadwal(item.id)} className="bg-green-600 text-white px-4 py-1.5 rounded-full text-xs font-bold">Ambil</button>
                        </td>
                      </tr>
                    ))}

                    {activeTab === "ambilan" && jadwalAmbilan.map((item, i) => (
                      <tr key={i} className="hover:bg-indigo-50/30 transition">
                        <td className="px-4 py-4 font-bold text-blue-600">{formatHariTanggal(item.tanggal)}</td>
                        <td className="px-4 py-4">{formatJam(item.jam_mulai)} – {formatJam(item.jam_selesai)}</td>
                        <td className="px-4 py-4 font-medium italic">{item.nama_mapel} <span className="block text-[10px] text-gray-400">Ganti: {item.nama_guru_asli}</span></td>
                        <td className="px-4 py-4 font-bold text-blue-700 uppercase">{item.nama_kelas}</td>
                        <td className="px-4 py-4 text-center">
                          <button onClick={() => navigate("/guru/presensi", { state: item })} className="bg-indigo-600 text-white px-4 py-1.5 rounded-full text-xs font-bold flex items-center gap-2 mx-auto"><FiClipboard /> Presensi</button>
                        </td>
                      </tr>
                    ))}

                    {activeTab === "status" && statusLaporan.map((item, i) => (
                      <tr key={i} className="hover:bg-gray-50 transition italic text-gray-500">
                        <td className="px-4 py-4 font-bold text-gray-800">{formatHariTanggal(item.tanggal)}</td>
                        <td className="px-4 py-4 text-xs">{formatJam(item.jam_mulai)} - {formatJam(item.jam_selesai)}</td>
                        <td className="px-4 py-4 not-italic font-medium">{item.nama_mapel}</td>
                        <td className="px-4 py-4 uppercase">{item.nama_kelas}</td>
                        <td className="px-4 py-4 not-italic font-semibold text-indigo-600">{item.nama_guru_pengganti || "—"}</td>
                        <td className="px-4 py-4 text-center">
                          <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase not-italic ${item.status === "diambil" ? "bg-green-100 text-green-600" : "bg-amber-100 text-amber-600"}`}>{item.status === "diambil" ? "Terisi" : "Menunggu"}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* MOBILE VIEW CARD */}
              <div className="md:hidden space-y-4">
                {activeTab === "jadwal" && filteredJadwal.map((item, i) => (
                  <div key={i} className="border p-4 rounded-xl bg-slate-50/50 space-y-2">
                    <div className="flex justify-between font-bold text-indigo-600 text-xs uppercase"><span>{item.hari}</span><span className="bg-indigo-100 px-2 py-0.5 rounded uppercase">{item.nama_kelas}</span></div>
                    <h4 className="font-bold text-gray-800">{item.nama_mapel}</h4>
                    <div className="flex justify-between items-center pt-2 border-t text-xs text-gray-500"><span>{formatJam(item.jam_mulai)} – {formatJam(item.jam_selesai)}</span><button onClick={() => { setSelJadwal(item); setShowModal(true); }} className="text-red-500 font-bold px-3 py-1.5 rounded-lg border border-red-100 bg-white">Lapor Izin</button></div>
                  </div>
                ))}
                
                {activeTab === "kosong" && jadwalKosong.map((item, i) => (
                  <div key={i} className="border border-amber-100 p-4 rounded-xl bg-amber-50/30 space-y-2">
                    <p className="text-[10px] font-bold text-amber-600 uppercase">{formatHariTanggal(item.tanggal)}</p>
                    <h4 className="font-bold text-gray-800">{item.nama_mapel} ({item.nama_kelas})</h4>
                    <div className="text-[11px] text-gray-500 italic">Guru: {item.nama_guru_asli}</div>
                    {item.alasan && <div className="text-[11px] text-red-500 italic">Alasan: {item.alasan}</div>}
                    <button onClick={() => handleAmbilJadwal(item.id)} className="w-full bg-green-600 text-white py-2 rounded-lg text-xs font-bold mt-2">Ambil Jadwal</button>
                  </div>
                ))}

                {activeTab === "ambilan" && jadwalAmbilan.map((item, i) => (
                  <div key={i} className="border border-blue-100 p-4 rounded-xl bg-blue-50/30 space-y-2">
                    <p className="text-[10px] font-bold text-blue-600 uppercase">{formatHariTanggal(item.tanggal)}</p>
                    <h4 className="font-bold italic text-gray-800">{item.nama_mapel} ({item.nama_kelas})</h4>
                    <button onClick={() => navigate("/guru/presensi", { state: item })} className="w-full bg-indigo-600 text-white py-2 rounded-lg text-xs font-bold mt-2 flex items-center justify-center gap-2"><FiClipboard /> Presensi</button>
                  </div>
                ))}

                {activeTab === "status" && statusLaporan.map((item, i) => (
                  <div key={i} className="border border-gray-100 p-4 rounded-xl bg-white space-y-2">
                    <div className="flex justify-between items-center"><span className="text-[10px] text-gray-400">{formatHariTanggal(item.tanggal)}</span><span className={`text-[10px] font-bold uppercase ${item.status === 'diambil' ? 'text-green-600' : 'text-amber-600'}`}>{item.status}</span></div>
                    <h4 className="font-bold text-gray-800">{item.nama_mapel} ({item.nama_kelas})</h4>
                    <div className="text-[10px] text-gray-500 flex items-center gap-1"><FiUser /> Pengisi: {item.nama_guru_pengganti || "—"}</div>
                  </div>
                ))}
                
                {/* EMPTY STATE */}
                {((activeTab === "jadwal" && filteredJadwal.length === 0) || (activeTab === "kosong" && jadwalKosong.length === 0) || (activeTab === "ambilan" && jadwalAmbilan.length === 0) || (activeTab === "status" && statusLaporan.length === 0)) && (
                   <div className="text-center py-10 text-gray-400 italic text-sm">Tidak ada data.</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* MODALS (LAPOR) */}
        {(showModal || showModalAll) && createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><FiAlertCircle className="text-red-500" /> {showModalAll ? "Izin Satu Hari Penuh" : "Konfirmasi Izin"}</h3>
              <input type="date" className="w-full border rounded-xl px-3 py-2 text-sm mb-3" value={showModalAll ? tanggalIzin : tanggalIzinSingle} min={new Date().toISOString().slice(0, 10)} onChange={(e) => showModalAll ? setTanggalIzin(e.target.value) : setTanggalIzinSingle(e.target.value)} />
              <textarea className="w-full border rounded-xl p-3 text-sm mb-4 outline-none focus:ring-2 focus:ring-indigo-500" rows="3" placeholder="Alasan izin..." value={alasan} onChange={(e) => setAlasan(e.target.value)} />
              <div className="flex gap-3"><button onClick={() => { setShowModal(false); setShowModalAll(false); }} className="flex-1 py-2 font-bold text-gray-500">Batal</button><button onClick={() => handleLapor(showModalAll ? "all" : "single")} disabled={!alasan.trim()} className="flex-1 py-2 bg-red-500 text-white rounded-xl font-bold disabled:opacity-50">Kirim</button></div>
            </div>
          </div>, document.body
        )}
      </div>
    </GuruLayout>
  );
}